import { useState, useEffect } from "react";
import { Sidebar, Menu, MenuItem, SubMenu } from "react-pro-sidebar";
import { Link, useLocation } from "react-router-dom";
import {
  Activity,
  BarChart3,
  Wrench,
  Building2,
  CalendarCheck,
  ClipboardList,
  Dumbbell,
  FileText,
  Inbox,
  Layers,
  Newspaper,
  LayoutGrid,
  Menu as MenuIcon,
  Paintbrush,
  ScrollText,
  Shield,
  Target,
  UserPlus,
  Users,
  UsersRound,
  X,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { usePermissions } from "../../context/Permissioncontext";
import { useBrand } from "../../context/BrandContext";
import defaultAppLogo from "../../assets/smarttt-default-logo.png";
import { resolveClubImageUrl } from "../../utils/resolveClubImageUrl";
import { canAccessClubBranding } from "../../utils/brandAccess";
import { canAccessGoalProposals } from "../../utils/goalProposalsAccess";
import { canAccessExerciseProposals } from "../../utils/exerciseProposalsAccess";
import { canAccessLevelMatrix } from "../../utils/levelBandsAccess";
import { canAccessConsentLedger } from "../../utils/consentLedgerAccess";
import { isSuperAdminUser } from "../../utils/brandAuth";
import { canShowAdminNavItem } from "../../utils/pilotAdminNav";
import { canSeeDashboardBottomNav, userHasModeratorRole } from "../../utils/homeRole";
import { openSwitchClubDialog } from "../../utils/switchActiveClubClient";
import {
  OPEN_ADMIN_SIDEBAR_EVENT,
  consumePendingOpenAdminSidebar,
} from "../../tour/tourEvents";
import "./SideBar.css";

function HallItem({ to, icon: Icon, label, active, tourId, onNavigate }) {
  return (
    <MenuItem
      component={
        <Link
          to={to}
          onClick={onNavigate}
          {...(tourId ? { "data-tour-id": tourId } : {})}
        />
      }
      icon={<Icon size={18} strokeWidth={2.1} aria-hidden="true" />}
      className={active ? "active" : ""}
    >
      {label}
    </MenuItem>
  );
}

function HallKicker({ children }) {
  return (
    <div className="hall-rail__kicker" role="presentation">
      {children}
    </div>
  );
}

export function SideBar() {
  const location = useLocation();
  const [activeMenu, setActiveMenu] = useState("dashboard");
  const [broken, setBroken] = useState(
    window.matchMedia("(max-width: 800px)").matches
  );
  const { t } = useTranslation();
  const info = JSON.parse(localStorage.getItem("user") || "{}");
  const { hasPermission } = usePermissions();
  const { brand } = useBrand();
  const isSuperAdmin = isSuperAdminUser(info);
  const show = (key) => canShowAdminNavItem(key, brand);
  const showCompetitions =
    show("competitions") &&
    !isSuperAdmin &&
    (Number(info?.type_user_id) === 2 ||
      Number(info?.type_user_id) === 3 ||
      Number(info?.type_user_id) === 6 ||
      info?.hasCommitteeRole === true ||
      info?.hasCoachRole === true ||
      info?.hasAdminRole === true ||
      info?.hasModeratorRole === true ||
      userHasModeratorRole(info) ||
      (Array.isArray(info?.all_role_names) &&
        info.all_role_names.some((n) =>
          ["Admin", "Committee Member", "Coach", "Moderator"].includes(n)
        )) ||
      (Array.isArray(info?.all_roles) &&
        info.all_roles.some((id) =>
          ["r2", "r3", "r9", "r10"].includes(String(id))
        )));

  useEffect(() => {
    setActiveMenu(location.pathname);
  }, [location]);

  const isActive = (path) =>
    activeMenu === path || activeMenu.startsWith(`${path}/`);
  const [toggled, setToggled] = useState(false);

  useEffect(() => {
    document.documentElement.classList.toggle("admin-sidebar-broken", broken);
    return () => document.documentElement.classList.remove("admin-sidebar-broken");
  }, [broken]);

  useEffect(() => {
    if (consumePendingOpenAdminSidebar()) setToggled(true);
    const sync = (event) => {
      setToggled(event?.detail?.open !== false);
    };
    window.addEventListener(OPEN_ADMIN_SIDEBAR_EVENT, sync);
    return () => window.removeEventListener(OPEN_ADMIN_SIDEBAR_EVENT, sync);
  }, []);

  const logoSrc = resolveClubImageUrl(brand.logo_url) || defaultAppLogo;
  const brandName = brand.brand_name || "SmartTT Club";
  const canSwitchClub =
    Number(info?.type_user_id) === 2 ||
    Number(info?.type_user_id) === 3 ||
    Number(info?.type_user_id) === 6 ||
    info?.hasAdminRole ||
    info?.hasCommitteeRole ||
    info?.hasCoachRole ||
    info?.hasModeratorRole ||
    userHasModeratorRole(info) ||
    isSuperAdmin;

  const closeIfMobile = () => {
    if (broken) setToggled(false);
  };

  const handleLogoClick = () => {
    if (canSwitchClub) openSwitchClubDialog();
  };

  const item = (props) => (
    <HallItem {...props} active={isActive(props.to)} onNavigate={closeIfMobile} />
  );

  const hallItems = show("dashboard") && canSeeDashboardBottomNav(info);
  const peopleItems = [
    show("users") && hasPermission("USER_LIST_VIEW") && {
      to: "/UserManagement",
      icon: Users,
      label: t("user"),
    },
    isSuperAdmin && {
      to: "/ClubInquiries",
      icon: Inbox,
      label: t("clubInquiriesTitle", "Inquiries"),
    },
    show("consent") && canAccessConsentLedger(info, hasPermission) && {
      to: "/Consent",
      icon: ClipboardList,
      label: t("consentLedgerTitle", "Player consent"),
    },
    show("invite") && hasPermission("PLAYER_INVITE") && {
      to: "/InviteUser",
      icon: UserPlus,
      label: t("invite"),
      tourId: "admin-nav-invite",
    },
  ].filter(Boolean);
  const trainingItems = [
    show("groups") && hasPermission("VIEW_GROUP") && {
      to: "/GroupList",
      icon: UsersRound,
      label: t("group"),
      tourId: "admin-nav-groups",
    },
    show("session_templates") && hasPermission("VIEW_GROUP") && {
      to: "/SessionTemplates",
      icon: FileText,
      label: t("sessionTemplates") || "Session Templates",
    },
    show("exercise_studio") && hasPermission("MANAGE_EXERCISES_STUDIO") && {
      to: "/ExerciseStudio",
      icon: Dumbbell,
      label: t("exerciseStudio") || "Exercise Studio",
    },
    show("goal_proposals") && canAccessGoalProposals(info) && {
      to: "/GoalProposals",
      icon: Target,
      label: t("goalProposalsTitle") || "Goal Proposals",
    },
    show("exercise_proposals") && canAccessExerciseProposals(info) && {
      to: "/ExerciseProposals",
      icon: Target,
      label: t("exerciseProposalsTitle") || "Exercise Proposals",
    },
    show("session_templates") && canAccessLevelMatrix(info) && {
      to: "/LevelClassification",
      icon: Layers,
      label: t("levelMatrixTitle") || "Level classification",
    },
  ].filter(Boolean);
  const clubItems = [
    show("club") && {
      to: "/Club",
      icon: Building2,
      label: t("club"),
    },
    showCompetitions && {
      to: "/Competitions",
      icon: CalendarCheck,
      label: t("competitions") || "Competitions",
    },
    {
      to: "/newsletter",
      icon: Newspaper,
      label: t("newsletterTitle", "Pingflo"),
    },
    show("branding") && canAccessClubBranding(info) && {
      to: "/BrandSettings",
      icon: Paintbrush,
      label: t("branding"),
    },
    show("table_rental") && canAccessClubBranding(info) && {
      to: "/TableRental",
      icon: CalendarCheck,
      label: t("rental_admin_title"),
    },
  ].filter(Boolean);
  const showAccess = show("access_control") && isSuperAdmin;
  const showBusinessIntelligence = isSuperAdmin;
  const showLegal =
    show("legal") &&
    (hasPermission("USER_CREATE") ||
      hasPermission("UPDATE_USER_INFO") ||
      hasPermission("DELETE_USER"));
  const legalItems = showLegal
    ? [
        hasPermission("USER_AGREEMENT") && {
          to: "/User_Agreement?lng=english_content",
          match: "/User_Agreement",
          icon: ScrollText,
          label: t("useragreement"),
        },
        hasPermission("TERMS_OF_USE") && {
          to: "/Termsofuse_Agreement?lng=english_content",
          match: "/Termsofuse_Agreement",
          icon: ClipboardList,
          label: t("terms"),
        },
      ].filter(Boolean)
    : [];

  return (
    <>
      <Sidebar
        className="hall-rail"
        width="232px"
        backgroundColor="transparent"
        toggled={toggled}
        customBreakPoint="800px"
        onBreakPoint={setBroken}
        onBackdropClick={() => setToggled(false)}
      >
        <button
          type="button"
          className={`hall-rail__club${canSwitchClub ? "" : " is-static"}`}
          onClick={handleLogoClick}
          title={canSwitchClub ? t("switchClubPrompt", "Switch club") : brandName}
        >
          <img src={logoSrc} alt="" id="site-logo" />
          <span className="hall-rail__club-copy">
            <span className="hall-rail__club-name">{brandName}</span>
            {canSwitchClub ? (
              <span className="hall-rail__club-hint">{t("hallNavSwitchClub")}</span>
            ) : null}
          </span>
        </button>

        <nav className="hall-rail__nav" aria-label={t("dashboard")}>
          <Menu>
          {hallItems ? (
            <>
              <HallKicker>{t("hallNavHall")}</HallKicker>
              {item({
                to: "/Dashboard",
                icon: LayoutGrid,
                label: t("dashboard"),
                tourId: "admin-nav-dashboard",
              })}
            </>
          ) : null}

          {peopleItems.length ? (
            <>
              <HallKicker>{t("hallNavPeople")}</HallKicker>
              {peopleItems.map((entry) => (
                <HallItem
                  key={entry.to}
                  {...entry}
                  active={isActive(entry.to)}
                  onNavigate={closeIfMobile}
                />
              ))}
            </>
          ) : null}

          {trainingItems.length ? (
            <>
              <HallKicker>{t("hallNavTraining")}</HallKicker>
              {trainingItems.map((entry) => (
                <HallItem
                  key={entry.to}
                  {...entry}
                  active={isActive(entry.to)}
                  onNavigate={closeIfMobile}
                />
              ))}
            </>
          ) : null}

          {clubItems.length ? (
            <>
              <HallKicker>{t("hallNavClub")}</HallKicker>
              {clubItems.map((entry) => (
                <HallItem
                  key={entry.to}
                  {...entry}
                  active={isActive(entry.to)}
                  onNavigate={closeIfMobile}
                />
              ))}
            </>
          ) : null}

          {showBusinessIntelligence ? (
            <>
              <HallKicker>Intelligence</HallKicker>
              {item({
                to: "/BusinessIntelligence",
                icon: BarChart3,
                label: "Business Intelligence",
              })}
              {item({
                to: "/CaptureQos",
                icon: Activity,
                label: "Capture QoS",
              })}
              {item({
                to: "/Fixcode",
                icon: Wrench,
                label: "Fix desk",
              })}
            </>
          ) : null}

          {showAccess ? (
            <SubMenu
              label={t("access_control")}
              icon={<Shield size={18} strokeWidth={2.1} aria-hidden="true" />}
              defaultOpen={
                isActive("/Roles") ||
                isActive("/Permissions") ||
                isActive("/TourGuide")
              }
            >
              <MenuItem
                component={<Link to="/Roles" onClick={closeIfMobile} />}
                className={isActive("/Roles") ? "active" : ""}
              >
                {t("roles")}
              </MenuItem>
              <MenuItem
                component={<Link to="/Permissions" onClick={closeIfMobile} />}
                className={isActive("/Permissions") ? "active" : ""}
              >
                {t("permission")}
              </MenuItem>
              <MenuItem
                component={<Link to="/TourGuide" onClick={closeIfMobile} />}
                className={isActive("/TourGuide") ? "active" : ""}
              >
                {t("tour_guide_title", "Guide tours")}
              </MenuItem>
            </SubMenu>
          ) : null}

          {legalItems.length ? (
            <>
              <HallKicker>{t("hallNavLegal")}</HallKicker>
              {legalItems.map((entry) => (
                <HallItem
                  key={entry.to}
                  to={entry.to}
                  icon={entry.icon}
                  label={entry.label}
                  active={isActive(entry.match)}
                  onNavigate={closeIfMobile}
                />
              ))}
            </>
          ) : null}
          </Menu>
        </nav>
      </Sidebar>
      {broken ? (
        <button
          type="button"
          className={`hall-rail__toggle${toggled ? " is-open" : ""}`}
          data-tour-id="admin-nav-toggle"
          aria-expanded={toggled}
          aria-label={toggled ? t("hallNavCloseMenu") : t("hallNavOpenMenu")}
          onClick={() => setToggled(!toggled)}
        >
          {toggled ? (
            <X size={18} strokeWidth={2.2} aria-hidden="true" />
          ) : (
            <MenuIcon size={18} strokeWidth={2.2} aria-hidden="true" />
          )}
        </button>
      ) : null}
    </>
  );
}
